import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthenticationGuard } from './guards/authentication.guard';
import { RoleGuard } from './guards/role.guard';
import { defaultRouteGuard, LoginComponent } from '@card-software/library';
import { BusinessesComponent } from './components/business/businesses/businesses.component';
import { InfoComponent } from './components/info/info.component';
import { BusinessDetailsComponent } from './components/business/business-details/business-details.component';
import { ProviderDetailsComponent } from './components/business/provider-details/provider-details.component';
import { EmployeesComponent } from './components/business/employees/employees.component';
import { EmployeeDetailsComponent } from './components/business/employee-details/employee-details.component';
import { PettyCashFundsComponent } from './components/petty-cash/funds/funds.component';
import { PettyCashFundDetailsComponent } from './components/petty-cash/fund-details/fund-details.component';
import { TripsComponent } from './components/petty-cash/trips/trips.component';
import { TripDetailsComponent } from './components/petty-cash/trip-details/trip-details.component';
import { ReceiptCaptureComponent } from './components/petty-cash/receipt-capture/receipt-capture.component';
import { ExpenseClaimsComponent } from './components/petty-cash/expense-claims/expense-claims.component';
import { ExpenseClaimDetailsComponent } from './components/petty-cash/expense-claim-details/expense-claim-details.component';
import { ExpenseSettlementsComponent } from './components/petty-cash/expense-settlements/expense-settlements.component';
import { ExpenseSettlementDetailsComponent } from './components/petty-cash/expense-settlement-details/expense-settlement-details.component';
import { EmployeeAdvancesComponent } from './components/petty-cash/employee-advances/employee-advances.component';
import { EmployeeAdvanceDetailsComponent } from './components/petty-cash/employee-advance-details/employee-advance-details.component';
import { ExchangeRateComponent } from './components/exchange-rate/exchange-rate.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { CostAnalysisPageComponent } from './components/cost-analysis/cost-analysis-page/cost-analysis-page.component';
import { ProcessDetailPageComponent } from './components/cost-analysis/process-detail-page/process-detail-page.component';
import { PoolsPageComponent } from './components/costs/pools/pools-page.component';
import { ReportsPageComponent } from './components/costs/reports/reports-page.component';
import {
  CostingRoles,
  BusinessRoles,
  ExchangeRateRoles,
  BankAccountRoles,
  BankTransactionRoles,
  AccountsPayableRoles,
  MonthlyUploadsRoles,
  CostCenterRoles,
  ExpenseConceptRoles,
  GlAccountRuleRoles,
  PettyCashRoles,
} from './models/roles/roles-group';
import { MENU_ITEMS } from './app-menu';
import { BankAccountComponent } from './components/bank-accounts/bank-account/bank-account.component';
import { BankAccountDetailsComponent } from './components/bank-accounts/bank-account-details/bank-account-details.component';
import { BankTransactionComponent } from './components/bank-transactions/bank-transaction/bank-transaction.component';
import { BankTransactionDetailsComponent } from './components/bank-transactions/bank-transaction-details/bank-transaction-details.component';
import { InternalTransfersComponent } from './components/internal-transfers/internal-transfers/internal-transfers.component';
import { InternalTransferDetailsComponent } from './components/internal-transfers/internal-transfer-details/internal-transfer-details.component';
import { BankStatementComponent } from './components/bank-statements/bank-statement/bank-statement.component';
import { BankReconciliationComponent } from './components/bank-reconciliation/bank-reconciliation/bank-reconciliation.component';
import { MonthlyUploadsComponent } from './components/monthly-uploads/monthly-uploads.component';
import { ApAgingComponent } from './components/accounts-payable/ap-aging/ap-aging.component';
import { ApDocumentsComponent } from './components/accounts-payable/ap-documents/ap-documents.component';
import { ApDocumentDetailsComponent } from './components/accounts-payable/ap-document-details/ap-document-details.component';
import { ApWithholdingDetailsComponent } from './components/accounts-payable/ap-withholding-details/ap-withholding-details.component';
import { ApWithholdingsComponent } from './components/accounts-payable/ap-withholdings/ap-withholdings.component';
import { WithholdingRemittanceComponent } from './components/accounts-payable/withholding-remittance/withholding-remittance.component';
import { ApPaymentsComponent } from './components/accounts-payable/ap-payments/ap-payments.component';
import { PoSettlementComponent } from './components/accounts-payable/po-settlement/po-settlement.component';
import { PoSettlementDetailsComponent } from './components/accounts-payable/po-settlement/po-settlement-details.component';
import { ApPaymentDetailsComponent } from './components/accounts-payable/ap-payment-details/ap-payment-details.component';
import { ApPaymentRequestsComponent } from './components/accounts-payable/ap-payment-requests/ap-payment-requests.component';
import { ApPaymentRequestDetailsComponent } from './components/accounts-payable/ap-payment-request-details/ap-payment-request-details.component';
import { ApAdvanceRequestsComponent } from './components/accounts-payable/ap-advance-requests/ap-advance-requests.component';
import { ApAdvanceRequestDetailsComponent } from './components/accounts-payable/ap-advance-request-details/ap-advance-request-details.component';
import { ApAdvancesComponent } from './components/accounts-payable/ap-advances/ap-advances.component';
import { ApAdvanceDetailsComponent } from './components/accounts-payable/ap-advance-details/ap-advance-details.component';
import { TaxAuthorizationsComponent } from './components/tax-authorizations/tax-authorizations.component';
import { CashFundingDetailsComponent } from './components/cash-funding/cash-funding-details/cash-funding-details.component';
import { CashFundingsComponent } from './components/cash-funding/cash-fundings/cash-fundings.component';
import { CashRequestsComponent } from './components/cash-request/cash-requests/cash-requests.component';
import { CashRequestDetailsComponent } from './components/cash-request/cash-request-details/cash-request-details.component';
import { ChartOfAccountsComponent } from './components/ledger/chart-of-accounts/chart-of-accounts.component';
import { JournalEntriesComponent } from './components/ledger/journal-entries/journal-entries.component';
import { JournalEntryDetailsComponent } from './components/ledger/journal-entry-details/journal-entry-details.component';
import { JournalEntryManualComponent } from './components/ledger/journal-entry-manual/journal-entry-manual.component';
import { AccountingPeriodsComponent } from './components/ledger/accounting-periods/accounting-periods.component';
import { ExpenseConceptsComponent } from './components/expense-concepts/expense-concepts.component';
import { CostCentersComponent } from './components/cost-centers/cost-centers.component';
import { GlAccountRulesComponent } from './components/gl-account-rules/gl-account-rules.component';
import { AccountSubledgerComponent } from './components/ledger/account-subledger/account-subledger.component';
import { PartyStatementComponent } from './components/ledger/party-statement/party-statement.component';
import { WithholdingTaxesComponent } from './components/withholding-taxes/withholding-taxes.component';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [AuthenticationGuard, defaultRouteGuard(MENU_ITEMS, 'Finance')],
    component: UnauthorizedComponent,
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    // The guide describes the product, not any tenant's data, so it carries no role guard — every
    // authenticated user can read how the flows work regardless of which screens they may open.
    path: 'info',
    component: InfoComponent,
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'info/:slug',
    component: InfoComponent,
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'businesses',
    component: BusinessesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Businesses_Title' }],
      roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
    },
  },
  {
    // Employees enabled for trips and advances (#5775), kept apart from businesses.
    path: 'employees',
    component: EmployeesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
    },
  },
  {
    path: 'employee-details',
    component: EmployeeDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
    },
  },
  {
    // Petty cash funds (#5785).
    path: 'petty-cash-funds',
    component: PettyCashFundsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'PettyCashFund_Title' }],
      roles: [PettyCashRoles.PettyCashFundViewer, PettyCashRoles.PettyCashFundAdmin, PettyCashRoles.PettyCashTreasurer],
    },
  },
  {
    path: 'petty-cash-fund-details',
    component: PettyCashFundDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'PettyCashFund_Title', route: '/petty-cash-funds' },
        { label: 'PettyCashFund_Details', awaitCurrent: true },
      ],
      roles: [PettyCashRoles.PettyCashFundViewer, PettyCashRoles.PettyCashFundAdmin, PettyCashRoles.PettyCashTreasurer],
    },
  },
  {
    // Employee trips (#5790).
    path: 'trips',
    component: TripsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Trip_Title' }],
      roles: [PettyCashRoles.TripViewer, PettyCashRoles.TripAdmin, PettyCashRoles.TripApprover],
    },
  },
  {
    path: 'trip-details',
    component: TripDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Trip_Title', route: '/trips' },
        { label: 'Trip_Details', awaitCurrent: true },
      ],
      roles: [PettyCashRoles.TripViewer, PettyCashRoles.TripAdmin, PettyCashRoles.TripApprover],
    },
  },
  {
    // Receipt capture (#5794): access is decided by the API per fund / trip (custodian, members, trip employee,
    // TripAdmin); the route only asks for a petty cash or trip role.
    path: 'expense-receipts',
    component: ReceiptCaptureComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Receipt_Title' }],
      roles: [
        PettyCashRoles.PettyCashFundViewer,
        PettyCashRoles.PettyCashFundAdmin,
        PettyCashRoles.TripViewer,
        PettyCashRoles.TripAdmin,
        PettyCashRoles.TripApprover,
        PettyCashRoles.ExpenseClaimViewer,
        PettyCashRoles.ExpenseClaimAdmin,
      ],
    },
  },
  {
    // Expense claims (#5800): who may open, edit or decide a claim is decided by the API (fund custodian, trip
    // employee or TripAdmin, frozen approvers); the route only asks for a petty cash, trip or claim role.
    path: 'expense-claims',
    component: ExpenseClaimsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'ExpenseClaim_Title' }],
      roles: [
        PettyCashRoles.PettyCashFundViewer,
        PettyCashRoles.PettyCashFundAdmin,
        PettyCashRoles.TripViewer,
        PettyCashRoles.TripAdmin,
        PettyCashRoles.TripApprover,
        PettyCashRoles.ExpenseClaimViewer,
        PettyCashRoles.ExpenseClaimAdmin,
        PettyCashRoles.ClaimApprover,
        PettyCashRoles.ClaimAccountingReviewer,
      ],
    },
  },
  {
    path: 'expense-claim-details',
    component: ExpenseClaimDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'ExpenseClaim_Title', route: '/expense-claims' },
        { label: 'ExpenseClaim_Details', awaitCurrent: true },
      ],
      roles: [
        PettyCashRoles.PettyCashFundViewer,
        PettyCashRoles.PettyCashFundAdmin,
        PettyCashRoles.TripViewer,
        PettyCashRoles.TripAdmin,
        PettyCashRoles.TripApprover,
        PettyCashRoles.ExpenseClaimViewer,
        PettyCashRoles.ExpenseClaimAdmin,
        PettyCashRoles.ClaimApprover,
        PettyCashRoles.ClaimAccountingReviewer,
      ],
    },
  },
  {
    // Expense settlements (#5799): the API lets PettyCashTreasurer create and post; readers come from petty cash,
    // trip and claim roles.
    path: 'expense-settlements',
    component: ExpenseSettlementsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'ExpenseSettlement_Title' }],
      roles: [
        PettyCashRoles.PettyCashTreasurer,
        PettyCashRoles.PettyCashFundViewer,
        PettyCashRoles.PettyCashFundAdmin,
        PettyCashRoles.TripViewer,
        PettyCashRoles.TripAdmin,
        PettyCashRoles.ExpenseClaimViewer,
        PettyCashRoles.ExpenseClaimAdmin,
      ],
    },
  },
  {
    path: 'expense-settlement-details',
    component: ExpenseSettlementDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'ExpenseSettlement_Title', route: '/expense-settlements' },
        { label: 'ExpenseSettlement_Details', awaitCurrent: true },
      ],
      roles: [
        PettyCashRoles.PettyCashTreasurer,
        PettyCashRoles.PettyCashFundViewer,
        PettyCashRoles.PettyCashFundAdmin,
        PettyCashRoles.TripViewer,
        PettyCashRoles.TripAdmin,
        PettyCashRoles.ExpenseClaimViewer,
        PettyCashRoles.ExpenseClaimAdmin,
      ],
    },
  },
  {
    // Employee advances (#5789): PettyCashTreasurer pays; trip readers see them.
    path: 'employee-advances',
    component: EmployeeAdvancesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'EmployeeAdvance_Title' }],
      roles: [PettyCashRoles.PettyCashTreasurer, PettyCashRoles.TripViewer, PettyCashRoles.TripAdmin],
    },
  },
  {
    path: 'employee-advance-details',
    component: EmployeeAdvanceDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'EmployeeAdvance_Title', route: '/employee-advances' },
        { label: 'EmployeeAdvance_Details', awaitCurrent: true },
      ],
      roles: [PettyCashRoles.PettyCashTreasurer, PettyCashRoles.TripViewer, PettyCashRoles.TripAdmin],
    },
  },
  {
    // Kept for links minted before #4892 moved the business onto a path parameter. The component reads
    // either shape, so an old `business-details?id=…` bookmark still lands on the right record.
    path: 'business-details',
    component: BusinessDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Businesses_Title', route: '/businesses' },
        { label: 'Business_Details', awaitCurrent: true },
      ],
      roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
    },
  },
  {
    // Provider details hang off the business (#4892) — declared before `business/:id` so the more
    // specific route wins.
    path: 'business/:id/provider-details',
    component: ProviderDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Businesses_Title', route: '/businesses' }, { label: 'Provider_Details_Title' }],
      roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
    },
  },
  {
    path: 'business/:id',
    component: BusinessDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Businesses_Title', route: '/businesses' },
        { label: 'Business_Details', awaitCurrent: true },
      ],
      roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
    },
  },
  {
    path: 'exchange-rate',
    component: ExchangeRateComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Exchange_Rates' }],
      roles: [ExchangeRateRoles.ExchangeRateViewer, ExchangeRateRoles.ExchangeRateAdmin],
    },
  },
  {
    path: 'cost-analysis',
    component: CostAnalysisPageComponent,
    canActivate: [AuthenticationGuard],
    data: {
      breadcrumb: [{ label: 'Cost_Analysis_Title' }],
    },
  },
  {
    path: 'cost-analysis/detail',
    component: ProcessDetailPageComponent,
    canActivate: [AuthenticationGuard],
    data: {
      breadcrumb: [
        { label: 'Cost_Analysis_Title', route: '/cost-analysis' },
        { label: 'Cost_Analysis_Process_Detail', awaitCurrent: true },
      ],
    },
  },
  {
    path: 'costs/pools',
    component: PoolsPageComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Costs_Pools_Title' }],
      roles: [CostingRoles.FinanceCalculatorAdmin],
    },
  },
  {
    path: 'costs/reports',
    component: ReportsPageComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [CostingRoles.FinanceCostingAdmin],
    },
  },
  {
    path: 'bank-accounts',
    component: BankAccountComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'BankAccount_Title' }],
      roles: [BankAccountRoles.BankAccountViewer, BankAccountRoles.BankAccountAdmin],
    },
  },
  {
    path: 'bank-account-details',
    component: BankAccountDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'BankAccount_Title', route: '/bank-accounts' },
        { label: 'BankAccount_Details_Breadcrumb_Details', awaitCurrent: true },
      ],
      roles: [BankAccountRoles.BankAccountViewer, BankAccountRoles.BankAccountAdmin],
    },
  },
  {
    path: 'bank-transactions',
    component: BankTransactionComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'BankTransaction_Title' }],
      roles: [BankTransactionRoles.BankTransactionViewer, BankTransactionRoles.BankTransactionAdmin],
    },
  },
  {
    path: 'bank-transaction-details',
    component: BankTransactionDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'BankTransaction_Title', route: '/bank-transactions' },
        { label: 'BankTransaction_Details_Breadcrumb_Details' },
      ],
      roles: [BankTransactionRoles.BankTransactionViewer, BankTransactionRoles.BankTransactionAdmin],
    },
  },
  {
    path: 'internal-transfers',
    component: InternalTransfersComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'InternalTransfer_Title' }],
      roles: [BankTransactionRoles.BankTransactionViewer, BankTransactionRoles.BankTransactionAdmin],
    },
  },
  {
    path: 'internal-transfer-details',
    component: InternalTransferDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'InternalTransfer_Title', route: '/internal-transfers' },
        { label: 'InternalTransfer_Breadcrumb_Details', awaitCurrent: true },
      ],
      roles: [BankTransactionRoles.BankTransactionViewer, BankTransactionRoles.BankTransactionAdmin],
    },
  },
  {
    path: 'bank-statements',
    component: BankStatementComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'BankStatement_Title' }],
      roles: [BankAccountRoles.BankAccountViewer, BankAccountRoles.BankAccountAdmin],
    },
  },
  {
    path: 'bank-reconciliation',
    component: BankReconciliationComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'BankReconciliation_Title' }],
      roles: [BankAccountRoles.BankAccountViewer, BankAccountRoles.BankAccountAdmin],
    },
  },
  {
    path: 'monthly-uploads',
    component: MonthlyUploadsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Monthly_Uploads_Title' }],
      roles: [MonthlyUploadsRoles.MonthlyUploadsViewer, MonthlyUploadsRoles.MonthlyUploadsAdmin],
    },
  },
  {
    path: 'ap-documents',
    component: ApDocumentsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'ApDocuments_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'ap-document-details',
    component: ApDocumentDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'ApDocuments_Title', route: '/ap-documents' },
        { label: 'ApDocument_Details', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'ap-withholding-details',
    component: ApWithholdingDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Withholdings_Title', route: '/ap-withholdings' },
        { label: 'Withholding_Details', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'ap-withholdings',
    component: ApWithholdingsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Withholdings_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'withholding-remittance',
    component: WithholdingRemittanceComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'ApDocuments_Title', route: '/ap-documents' }, { label: 'WithholdingRemittance_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'ap-payments',
    component: ApPaymentsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'ApPayments_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'ap-payment-details',
    component: ApPaymentDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'ApPayments_Title', route: '/ap-payments' },
        { label: 'ApPayment_Details', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'ap-payment-requests',
    component: ApPaymentRequestsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'ApPaymentRequests_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin, AccountsPayableRoles.AccountsPayableApprover],
    },
  },
  {
    path: 'ap-payment-request-details',
    component: ApPaymentRequestDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'ApPaymentRequests_Title', route: '/ap-payment-requests' },
        { label: 'ApPaymentRequest_Details', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin, AccountsPayableRoles.AccountsPayableApprover],
    },
  },
  {
    path: 'po-settlement',
    component: PoSettlementComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'PoSettlement_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'po-settlement-details',
    component: PoSettlementDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'AccountsPayable' }, { label: 'PoSettlement_Title', route: '/po-settlement' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'ap-advance-requests',
    component: ApAdvanceRequestsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'ApAdvanceRequests_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin, AccountsPayableRoles.AccountsPayableApprover],
    },
  },
  {
    path: 'ap-advance-request-details',
    component: ApAdvanceRequestDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'ApAdvanceRequests_Title', route: '/ap-advance-requests' },
        { label: 'ApAdvanceRequest_Details', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin, AccountsPayableRoles.AccountsPayableApprover],
    },
  },
  {
    path: 'ap-advances',
    component: ApAdvancesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'ApAdvances_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'ap-advance-details',
    component: ApAdvanceDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'ApAdvances_Title', route: '/ap-advances' },
        { label: 'ApAdvance_Details', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'tax-authorizations',
    component: TaxAuthorizationsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'TaxAuthorization_Title' }],
      roles: [BusinessRoles.BusinessViewer, BusinessRoles.BusinessAdmin],
    },
  },
  {
    path: 'cash-fundings',
    component: CashFundingsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'CashFunding_Dashboard_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'cash-funding-details',
    component: CashFundingDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'CashFunding_Dashboard_Title', route: '/cash-fundings' },
        { label: 'CashFunding_Details', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'cash-requests',
    component: CashRequestsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'CashRequest_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'cash-request-details',
    component: CashRequestDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'CashRequest_Title', route: '/cash-requests' },
        { label: 'CashRequest_Details', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'journal-entries',
    component: JournalEntriesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'JournalEntries_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'journal-entry-details',
    component: JournalEntryDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'JournalEntries_Title', route: '/journal-entries' },
        { label: 'JournalEntry', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    // #5077 — manual entry. `?id=` opens an existing draft; no id (or `new`) starts a blank one.
    path: 'journal-entry-manual',
    component: JournalEntryManualComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'JournalEntries_Title', route: '/journal-entries' },
        { label: 'JournalEntry_Manual_New', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'chart-of-accounts',
    component: ChartOfAccountsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'COA_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'accounting-periods',
    component: AccountingPeriodsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'AP_Period_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'cost-centers',
    component: CostCentersComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [CostCenterRoles.CostCenterViewer],
    },
  },
  {
    path: 'expense-concepts',
    component: ExpenseConceptsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [ExpenseConceptRoles.ExpenseConceptViewer, ExpenseConceptRoles.ExpenseConceptAdmin],
    },
  },
  {
    path: 'gl-account-rules',
    component: GlAccountRulesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [GlAccountRuleRoles.GlAccountRuleViewer, GlAccountRuleRoles.GlAccountRuleAdmin],
    },
  },
  {
    path: 'chart-of-accounts/subledger',
    component: AccountSubledgerComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'COA_Title', route: '/chart-of-accounts' },
        { label: 'COA_Subledger_Breadcrumb', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'chart-of-accounts/party',
    component: PartyStatementComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'COA_Title', route: '/chart-of-accounts' },
        { label: 'COA_PartyStatement_Breadcrumb', awaitCurrent: true },
      ],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'withholding-taxes',
    component: WithholdingTaxesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Withholding_Taxes' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: 'ap-aging',
    component: ApAgingComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'ApAging_Title' }],
      roles: [AccountsPayableRoles.AccountsPayableAdmin],
    },
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
